import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { CartIcon } from "@/components/shop/CartIcon";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="37467" className="hidden lg:flex items-center gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative py-2 text-sm font-semibold uppercase tracking-wide text-foreground transition-colors hover:text-primary font-sans">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex flex-col bg-background lg:hidden animate-in slide-in-from-right duration-300">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <Link to="/HomePage" onClick={onClose}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1594097/img/hsinhui_logo.png" alt="HSINHUI COSMETICS" className="h-10 w-auto object-contain" />
        </Link>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="size-6 text-foreground" />
        </Button>
      </div>
      
      <div className="flex-1 overflow-y-auto px-6 py-8">
        {loading ? <div className="space-y-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-muted animate-pulse rounded-sm" />)}
          </div> : <nav className="flex flex-col space-y-6">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-semibold uppercase tracking-wide text-foreground hover:text-primary font-sans" onClick={onClose}>
                {item.label}
              </Link>)}
          </nav>}
        
        <div className="mt-8 border-t border-border pt-8">
          <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-semibold uppercase tracking-wide" asChild>
            <Link to="/ContactUsPage" onClick={onClose}>
              Inquire Now
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="corporate-identity-header" className={`sticky top-0 z-40 w-full transition-all duration-300 border-b ${isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm" : "bg-background border-border"}`}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-8">
        {}
        <Link to="/HomePage" className="flex-shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1594097/img/hsinhui_logo.png" alt="HSINHUI COSMETICS" className="h-12 w-auto object-contain" />
        </Link>

        {}
        <MenuProvider menu_id="22" className="hidden lg:block">
          <DesktopMenu />
        </MenuProvider>

        {}
        <div className="flex items-center gap-4">
          <div className="hidden md:block">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold uppercase tracking-wide rounded-sm">
              <Link to="/ContactUsPage">
                Inquire Now
              </Link>
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <CartIcon className="text-foreground hover:text-primary transition-colors" iconSize="size-6" />
            
            {}
            <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:text-primary" onClick={() => setIsMobileMenuOpen(true)}>
              <Menu className="size-6" />
              <span className="sr-only">Open menu</span>
            </Button>
          </div>
        </div>
      </div>

      {}
      <MenuProvider menu_id="22">
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}